import {
  Box,
  Button,
  FormControl,
  Heading,
  IconButton,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  useBoolean,
} from '@chakra-ui/react'
import { AvailableAddons, InsurerProductAddition, InsurerProductPartAvailable, Offer } from 'BrokerTsLib/src/dtos'
import { Insurer, Risk } from 'BrokerTsLib/src/enums'
import React, { useContext, useEffect, useState } from 'react'
import { FormLabel } from '../../../../components/FormLabel'
import { Loading } from '../../../../components/Loading'
import { useGetOfferAvailableAddons, useSaveOfferAddons } from '../../../../hooks/useOffer'
import { Refresh, Selected, Settings, Subtract } from '../../../../icons'
import { insuranceParamsContext } from '../../../../layouts/Wizard'
import { GetOfferElementId, scrollToOfferWithOffset as scrollToOffer } from '../../OfferCar'
import { CheckboxSelect, CheckboxSelectItemProps } from '../../../../components/CheckboxSelect'
import { carkulkaOffersContext } from '../../../../context/CarkulkaOffersProvider'

const ExtraInsuranceContent = ({ children }: { children: React.ReactNode }) => {
  return <Box sx={{ display: 'grid', gap: 4 }}>{children}</Box>
}

const ExtraInsuranceSection = ({
  label,
  insurer,
  selectedInsurerProductId,
  selectedInsurerInternalId,
  onSelectAddon,
  selected = false,
  group,
  items,
  isDisabled = false
}: {
  children?: React.ReactNode | string
  label: string
  selected?: boolean
  insurer: Insurer
  selectedInsurerProductId?: number
  selectedInsurerInternalId?: string
  onSelectAddon: (riskId: number, insurerProductId?: number, insurerInternalId?: string) => void
  contentHeading?: string
  group: InsurerProductAddition
  items: InsurerProductPartAvailable[]
  isDisabled?: boolean
}) => {
  const iconSize = '20px'
  const [isOpen, setIsOpen] = useBoolean(false) // vyber varianty pripojisteni

  const onSelectAddonVartiant = (insurerProductId?: number, insurerInternalId?: string) => {
    onSelectAddon(group.riskId, insurerProductId, insurerInternalId)
    setIsOpen.off()
  }

  const hasContent = (items?.length ?? 0) > 0

  const onDeselectClick = () => onSelectAddonVartiant(undefined, undefined)

  const prodId = selectedInsurerProductId ?? '';

  let tooltip = ''

  if (prodId !== '') {
    const prods = items.filter(item => prodId == item.insurerProductId)
    if (prods.length > 0) {
      tooltip = prods[0].description ?? ''
    }
  }

  let allowRemove = true

  const selectedOptionValue = selectedInsurerProductId ?? ''
  let selectOptions: CheckboxSelectItemProps[] = []
  let addWithoutInsurance = true
  if (insurer == Insurer.GeneraliCeskaPojistovna && group.riskId == Risk.BALICEK) {
    addWithoutInsurance = false
    allowRemove = false
  }
  if (addWithoutInsurance) {
    selectOptions.push({
      label: 'Bez připojištění',
      value: '',
      defaultSelected: false
    })
  }
  const options = items.map<CheckboxSelectItemProps>((item) => ({
    label: item.name,
    value: item.insurerProductId?.toString() ?? '',
    tooltipText: item.description,
    defaultSelected: false
  })
  )
  selectOptions.push(...options)
  const opts = selectOptions.filter(item => item.value == selectedOptionValue)
  if (opts.length > 0) {
    opts[0].defaultSelected = true
  }

  return (
    <Box sx={{ display: 'grid', gap: 2, position: 'relative', maxWidth: '75rem' }}>

      <Popover
        isOpen={isOpen}
        onClose={setIsOpen.off}
      // returnFocusOnClose={false}
      // placement="start-end"
      >

        <PopoverAnchor>
          <Box
            sx={{
              display: 'flex',
              p: 2,
              justifyContent: 'space-between',
              border: '1px solid',
              borderColor: 'gray.200',
              borderRadius: 'md',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, placeItems: 'center' }}>
              {selected && (
                <IconButton colorScheme="white" aria-label="Odebrat" isDisabled={isDisabled} onClick={onDeselectClick}>
                  <Selected color="green.500" width={iconSize} height={iconSize} />
                </IconButton>
              )}
              <Text>{label}</Text>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, placeItems: 'center' }}>
              {selected && allowRemove && (
                <IconButton colorScheme="white" aria-label="Odebrat" isDisabled={isDisabled} onClick={onDeselectClick}>
                  <Subtract color="red.500" width={iconSize} height={iconSize} />
                </IconButton>
              )}

              {hasContent && (
                <PopoverTrigger>
                  <IconButton colorScheme="white" aria-label="Nastavit" isDisabled={isDisabled} onClick={(e) => {
                    setIsOpen.toggle()
                  }}>
                    <Settings color="primary.500" width={iconSize} height={iconSize} />
                  </IconButton>
                </PopoverTrigger>
              )}
            </Box>
          </Box>
        </PopoverAnchor>

        <Portal>
          <PopoverContent
            sx={{ maxWidth: '75rem', maxHeight: '60vh', width: '100%', height: '100%' }}
          >
            <PopoverArrow />
            <PopoverCloseButton onClick={setIsOpen.off} />

            {label && (
              <PopoverHeader>
                <Heading sx={{ fontSize: 'lg' }} as="h3">
                  {label}
                </Heading>
              </PopoverHeader>
            )}

            <PopoverBody sx={{ maxHeigth: '100%', overflow: 'auto' }}>
              {hasContent && (
                <ExtraInsuranceContent>
                  {group.description && hasContent &&
                    <Box dangerouslySetInnerHTML={{ __html: group.description }}></Box>}

                  {hasContent &&
                    <FormControl>
                      <FormLabel>Vyberte variantu</FormLabel>

                      <CheckboxSelect
                        options={selectOptions}
                        onChange={(value: string) => onSelectAddonVartiant(parseInt(value), undefined)}
                        disabled={isDisabled}
                      />
                    </FormControl>}
                </ExtraInsuranceContent>
              )}
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </Box>
  )
}

export const ExtraInsuranceTab = ({ offer, onSavedHandler }: { offer: Offer, onSavedHandler: Function }) => {
  const { recalculateOffers: recalculate } = useContext(carkulkaOffersContext)
  const { insuranceParamsId: paramsId, insuranceParams } = useContext(insuranceParamsContext)!
  const isContracted = !!insuranceParams?.generalParams.contractNumber
  const { refetch, data, isLoading } = useGetOfferAvailableAddons(offer.offerId, { enabled: false })
  const availableAddons = (data?.data ?? []) as AvailableAddons[]

  useEffect(() => { refetch() }, [])

  const { mutateAsync: saveAddons } = useSaveOfferAddons(offer.offerId)

  const [addons, setAddons] = useState<any>({})
  useEffect(() => {
    const defaultValues: any = {}

    offer.parts
      ?.filter(x => x.riskType == 3)
      .forEach(x => { defaultValues[x.riskId] = { riskId: x.riskId, insurerProductId: x.productId, insurerInternalId: x.insurerInternalId } })

    setAddons({ ...defaultValues })
  }, [offer.parts.length]);

  const onSelectAddon = (riskId: number, insurerProductId?: number, insurerInternalId?: string) => {
    setAddons((x: any) => { return { ...x, [riskId]: { riskId, insurerProductId, insurerInternalId } } })
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const submitAddons = () => {
    setIsSubmitting(true)

    let arr: InsurerProductPartAvailable[] = []
    Object.keys(addons).forEach(x => arr.push(addons[x]))

    saveAddons(arr)
      .then((resp) => {
        onSavedHandler()
        recalculate([offer.offerId], true)
          .then(() => setTimeout(() => scrollToOffer(GetOfferElementId(offer.offerId)), 100))
      })
      .finally(() => { setIsSubmitting(false) })
  }

  return (
    <>
      {isLoading && <Loading large={true} textAlign='center' />}
      {!isLoading &&
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { base: '1fr', lg: 'repeat(4, 1fr)' },
            columnGap: 4,
            rowGap: 2,
          }}
        >
          {(availableAddons.length ?? 0) == 0 && <Text>Žádné připojištění není k dispozici</Text>}
          {(availableAddons.length ?? 0) > 0 && availableAddons
            .sort((x, y) => x.group.displayOrder - y.group.displayOrder)
            .map(x => {
              return (
                <ExtraInsuranceSection
                  key={x.group.riskId}
                  label={x.group.name}
                  insurer={offer.insurerId}
                  selected={!!(addons[x.group.riskId]?.insurerProductId)}
                  selectedInsurerProductId={addons[x.group.riskId]?.insurerProductId}
                  selectedInsurerInternalId={addons[x.group.riskId]?.insurerInternalId}
                  onSelectAddon={onSelectAddon}
                  group={x.group}
                  items={x.items}
                  isDisabled={isContracted}
                />
              )
            })}

          {!isContracted && <Button
            variant="outline"
            isDisabled={isSubmitting}
            isLoading={isSubmitting}
            loadingText="Přepočítávám..."
            onClick={submitAddons}
            gridColumnStart={1}>
            <Refresh color="secondary.500" />
            Uložit a přepočítat
          </Button>}
        </Box >
      }
    </>
  )
}